<template>
  <div class="free-slider space-y-3">
    <div>
      <slot name="title" />
    </div>
    <div ref="sliderBody" class="relative"></div>
    <div>
      <slot name="labels" v-bind="{ labels: sliderLabels }" />
    </div>
  </div>
</template>

<script setup lang="ts">
  import { onMounted, ref } from 'vue';
  import type { API, Options, target } from 'nouislider';
  import noUiSlider from 'nouislider';
  import { DEFAULT_CONFIGS } from './configs';

  const emit = defineEmits([
    'change:modelValue',
    'update:modelValue',
    'onStart',
  ]);

  const props = withDefaults(
    defineProps<{
      configs: Options;
    }>(),
    {
      configs: () => DEFAULT_CONFIGS,
    },
  );
  const sliderBody = ref<target>();
  const sliderLabels = ref<(number | string)[]>([]);
  const sliderAPI = ref<API>();

  onMounted(() => {
    if (sliderBody.value) {
      sliderAPI.value = noUiSlider.create(sliderBody.value, {
        cssPrefix: 'free-slider__',
        ...props.configs,
      });

      sliderAPI.value.on('start', function () {
        emit('onStart');
      });

      sliderAPI.value.on('update', function (values) {
        sliderLabels.value = values;
        emit('change:modelValue', values);
      });

      sliderAPI.value.on('end', function (values) {
        emit('update:modelValue', values);
      });
    }
  });

  defineExpose({
    sliderAPI,
  });
</script>

<style>
  .free-slider__controls {
    @apply relative;
  }

  .free-slider__target {
    padding-left: var(--slider-target-padding-left, theme('padding.4'));
    padding-right: var(--slider-target-padding-right, theme('padding.4'));
  }

  .free-slider__target,
  .free-slider__target * {
    -webkit-touch-callout: none;
    -webkit-user-select: none;
    -ms-touch-action: none;
    touch-action: none;
    -ms-user-select: none;
    -moz-user-select: none;
    user-select: none;
    -moz-box-sizing: border-box;
    box-sizing: border-box;
  }

  .free-slider__base,
  .free-slider__connects {
    @apply relative z-[1] h-full w-full rounded-[85px];
  }

  .free-slider__connects {
    @apply overflow-hidden;

    height: var(--connection-height, 6px);
    background: var(
      --connection-bg-color,
      theme('colors.situational.lightgrey')
    );
  }

  .free-slider__connect,
  .free-slider__origin {
    @apply relative z-[1] will-change-transform;
    -ms-transform-origin: 0 0;
    -webkit-transform-origin: 0 0;
    -webkit-transform-style: preserve-3d;
    transform-origin: 0 0;
    transform-style: flat;
  }

  .free-slider__connect {
    height: var(--connection-height, 6px);
    background: var(--connection-color, theme('colors.green.500'));
  }

  .free-slider__handle {
    @apply bg-background-neutral-secondary absolute -top-0.5 -translate-y-1/2 cursor-pointer rounded-full border-4;

    height: var(--thumb-size, 1.5rem);
    width: var(--thumb-size, 1.5rem);
    border-color: var(--thumb-border-color, theme('colors.green.500'));
    background: var(--thumb-color, theme('colors.white'));
    /* eslint-disable-next-line max-len */
    box-shadow: 0px 4px 4px rgba(0, 0, 0, 0.1),
      0px 92px 92px rgba(0, 0, 0, 0.05),
      0px 3px 38.4354px rgba(0, 0, 0, 0.0334858),
      0px -3px 20.5494px rgba(0, 0, 0, 0.06),
      0px 5.79369px 11.5198px rgba(0, 0, 0, 0.0189792),
      0px 2.40368px 6.11809px rgba(0, 0, 0, 0.0132742),
      0px 0.705169px 2.54588px rgba(0, 0, 0, 0.00743532);
    -webkit-backface-visibility: hidden;
    backface-visibility: hidden;
    will-change: left;
  }

  .free-slider__handle-lower {
    right: calc(-1 * var(--slider-handler-lower-right, theme('spacing.2')));
  }

  .free-slider__handle-upper {
    right: calc(-1 * var(--slider-handler-upper-right, theme('spacing.4')));
  }

  .free-slider__txt-dir-rtl.free-slider__horizontal .free-slider__origin {
    @apply left-0 right-auto;
  }

  .free-slider__horizontal .free-slider__origin {
    @apply h-0;
  }

  .free-slider__touch-area {
    height: var(--thumb-size, 1.5rem);
    width: var(--thumb-size, 1.5rem);
  }

  .free-slider__state-tap .free-slider-connect,
  .free-slider__state-tap .free-slider-origin {
    -webkit-transition: transform 0.3s;
    transition: transform 0.3s;
  }

  .free-slider__value {
    @apply absolute whitespace-nowrap text-center;
  }
</style>
